/**
 * Sandbox MFE Plugin — Embedded Mode Setup
 *
 * This file contains all logic for running Sandbox as a micro-frontend (MFE)
 * plugin inside StudentPortal. It exposes `window.SandboxApp` so the portal's
 * SandboxRenderer can mount and unmount the Sandbox app on demand.
 *
 * HOW TO REMOVE THE MFE ARCHITECTURE (when retiring StudentPortal):
 *   1. Delete this file (src/mfe/sandboxPlugin.js).
 *   2. In src/index.js, delete the embedded-mode block and the import of this file.
 *   3. Delete src/publicPath.js if it is no longer needed.
 *   4. Remove the `window.__SANDBOX_IS_PLUGIN__` / `window.__SANDBOX_PUBLIC_PATH__`
 *      setup from StudentPortal's SandboxRenderer.
 */

import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { Router } from "react-router-dom";
import { StylesProvider } from "@material-ui/core/styles";
import { GoogleOAuthProvider } from "@react-oauth/google";
import svgIconSprite from "../SharedComponents/SvgIcons/svgIcons.sprite.svg";
import App from "../App";
import { setNavigateToPlatform, clearNavigateToPlatform } from "./navigationBridge";
import { setTokenRefreshHandler, clearTokenRefreshHandler } from "./tokenRefreshBridge";
import { createEmbeddedHistory } from "./createEmbeddedHistory";
import { sandboxGenerateClassName } from "./jssClassNameGenerator";

// ---------------------------------------------------------------------------
// React version-safe render helpers (supports React 16/17 and React 18)
// ---------------------------------------------------------------------------
function renderApp(container, component) {
  if (ReactDOM.createRoot) {
    const root = ReactDOM.createRoot(container);
    root.render(component);
    return root;
  }
  ReactDOM.render(component, container);
  return null;
}

function unmountApp(container, root) {
  if (root && typeof root.unmount === "function") {
    root.unmount();
  } else {
    ReactDOM.unmountComponentAtNode(container);
  }
}

// ---------------------------------------------------------------------------
// SVG sprite preloading
// In embedded mode, SVG <use href="url#id"> is blocked cross-origin by browsers.
// Fetch the sprite and inline it into the DOM so local #id references work.
// ---------------------------------------------------------------------------
function preloadSvgSprite() {
  return fetch(svgIconSprite)
    .then(function (r) { return r.text(); })
    .then(function (text) {
      if (document.getElementById("sandbox-svg-sprite")) return;
      var div = document.createElement("div");
      div.innerHTML = text;
      var svg = div.querySelector("svg");
      if (svg) {
        svg.style.display = "none";
        svg.id = "sandbox-svg-sprite";
        document.body.insertBefore(svg, document.body.firstChild);
      }
    })
    .catch(function () {});
}

// ---------------------------------------------------------------------------
// Font injection
// Sandbox's public/index.html is not used when embedded inside Portal, so
// required Google Fonts must be injected at runtime.
// ---------------------------------------------------------------------------
function injectFonts() {
  if (document.querySelector("link[data-sandbox-fonts]")) return;
  const fontLink = document.createElement("link");
  fontLink.rel = "stylesheet";
  fontLink.href =
    "https://fonts.googleapis.com/css2?family=Poppins&family=Inter:wght@300;400;500;600;700;800;900&family=Roboto&family=Quicksand&family=Bowlby+One+SC&display=block";
  fontLink.dataset.sandboxFonts = "true";
  document.head.appendChild(fontLink);
}

/**
 * Sets up the Sandbox MFE plugin API on `window.SandboxApp`.
 * Call this in index.js when `window.__SANDBOX_IS_PLUGIN__` is true.
 * @param {object} store - The Redux store instance.
 */
export function setupSandboxPlugin(store) {
  injectFonts();
  const spriteReady = preloadSvgSprite();

  let embeddedHistory = null;

  // Allows SandboxRenderer to sync the internal memory history when the portal
  // URL changes (browser back/forward, or any portal navigation that lands on a
  // sandbox route) and to force the old "/home" homepage onto the new dashboard.
  //
  // NOTE: this MUST be (re)installed on every mount(), not just once here.
  // unmount() nulls it for a clean teardown; if we only assigned it at setup
  // time, the first unmount would leave it null forever and every subsequent
  // mount would have a dead URL-sync + dead /home-suppression — causing the
  // portal to sit on /dashboard while the embedded app is stuck on /home until
  // a full page reload. It reads the closure `embeddedHistory`, which mount()
  // reassigns and unmount() nulls, so it stays a safe no-op while unmounted.
  function installHistorySync() {
    window.__SandboxHistorySync = (sandboxPath) => {
      if (embeddedHistory && embeddedHistory.location.pathname !== sandboxPath) {
        // Use __originalReplace to avoid triggering onSandboxNavigate again (echo loop)
        embeddedHistory.__originalReplace(sandboxPath);
      }
    };
  }

  window.SandboxApp = {
    /**
     * Mount the Sandbox app into a DOM element.
     * @param {string} containerId - id of the mount target (e.g. "sandbox-root")
     * @param {object} options
     * @param {string} options.initialPath - route Sandbox should render on mount
     * @param {function} [options.onNavigate] - callback for portal route navigation
     * @param {function} [options.onSandboxNavigate] - called when Sandbox pushes a route, so Portal updates the URL
     * @param {boolean} [options.hideNav] - true to hide Sandbox's own nav chrome
     * @param {function} [options.onTokenRefreshNeeded] - called when Sandbox needs a token refresh (MFE delegation)
     */
    mount(containerId, { initialPath, onNavigate, onSandboxNavigate, hideNav, onTokenRefreshNeeded }) {
      const el = document.getElementById(containerId);
      if (!el) {
        console.error(
          `[SandboxApp] mount failed: element with id "${containerId}" not found`
        );
        return;
      }

      // Register the portal's navigation callback so SPDashboard components
      // can trigger portal-level route changes via useSPNavigate.
      setNavigateToPlatform(onNavigate);
      setTokenRefreshHandler(onTokenRefreshNeeded);

      // Create the embedded history bridge — intercepts push/replace/goBack
      // and notifies portal so the real URL stays in sync.
      embeddedHistory = createEmbeddedHistory({ initialPath, onSandboxNavigate });

      // (Re)install the portal→sandbox history-sync hook for this mount. A
      // previous unmount nulls it, so it must be restored here or the URL sync
      // and /home-suppression stay dead for every mount after the first.
      installHistorySync();

      // Wait for SVG sprite to be inlined before rendering so icons work on first paint.
      spriteReady.then(() => {
        const root = renderApp(
          el,
          <React.StrictMode>
            {/* Seeded JSS generator so Sandbox's MUI v4 class names (sbx-jss*)
                never collide with the portal's (jss*) on the shared page.
                injectFirst puts MUI's <style> at the TOP of <head> so the app's
                own CSS-module/chunk CSS (inserted later) wins same-specificity
                ties — e.g. the Carousel's `.slide { flex: 0 0 100% }` must beat
                makeStyles' default `carouselItem { flex: 0 0 auto }`. Without
                this, embedded inserts MUI's JSS last and the default wins,
                stretching carousel slides. Standalone already loads chunk CSS
                after JSS, so it's correct there without injectFirst. */}
            <StylesProvider injectFirst generateClassName={sandboxGenerateClassName}>
              <Provider store={store}>
                <GoogleOAuthProvider
                  clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}
                >
                  <Router history={embeddedHistory}>
                    <App onNavigate={onNavigate} hideNav={hideNav} />
                  </Router>
                </GoogleOAuthProvider>
              </Provider>
            </StylesProvider>
          </React.StrictMode>
        );
        el._sandboxRoot = root;
      });
    },

    /**
     * Unmount the Sandbox app and clean up.
     * @param {string} containerId - same id passed to mount()
     */
    unmount(containerId) {
      const el = document.getElementById(containerId);
      if (el) {
        unmountApp(el, el._sandboxRoot);
        el._sandboxRoot = null;
      }
      clearNavigateToPlatform();
      clearTokenRefreshHandler();
      embeddedHistory = null;
      window.__SandboxHistorySync = null;
    },
  };
}
